import { ColDef, IAggFunc, IAggFuncParams } from 'ag-grid-community';
import { GESTIONNAIRE_FIELD } from 'business/constants';
import {
    AMOUNT_AMENDING_DECISION_FIELD,
    AMOUNT_AVAILABLE_FIELD,
    AMOUNT_ENGAGED_UNPAID,
    AMOUNT_SPECIAL_AUTHORIZATION_FIELD,
    AMOUNT_VOTED_SUP_FIELD,
    ANALYTIC_FIELD,
    ANALYTIC_NAME_FIELD,
    ANALYTIC_POST_0_CODE,
    ANALYTIC_POST_0_NAME,
    ANALYTIC_POST_1_CODE,
    ANALYTIC_POST_1_NAME,
    ANALYTIC_POST_2_CODE,
    ANALYTIC_POST_2_NAME,
    ANALYTIC_POST_3_CODE,
    ANALYTIC_POST_3_NAME,
    ANALYTIC_POST_4_CODE,
    ANALYTIC_POST_4_NAME,
    ANALYTIC_POST_5_CODE,
    ANALYTIC_POST_5_NAME,
    ANALYTIC_POST_6_CODE,
    ANALYTIC_POST_6_NAME,
    ANALYTIC_POST_7_CODE,
    ANALYTIC_POST_7_NAME,
    ANALYTIC_POST_8_CODE,
    ANALYTIC_POST_8_NAME,
    ANALYTIC_POST_9_CODE,
    ANALYTIC_POST_9_NAME,
    ANTENNA_FIELD,
    ANTENNA_NAME_FIELD,
    AP_CODE,
    AP_LABEL,
    AP_START_YEAR,
    BUDGET_CODE_FIELD,
    BUDGET_LINE_CODE,
    BUDGET_LINE_PROGRAM_CODE,
    BUDGET_LINE_PROGRAM_NAME,
    BUDGET_NAME_FIELD,
    BUDGET_YEAR_FIELD,
    CHAPTER_FIELD,
    CHAPTER_LABEL_FIELD,
    DIRECTION_CODE,
    DIRECTION_LABEL,
    ENVELOPPE_CODE,
    ENVELOPPE_LABEL,
    EVOLUTION_N_N_1,
    EXPENSE_INCOME_FIELD,
    FUNCTION_FIELD,
    FUNCTION_LABEL_FIELD,
    GESTIONNAIRE_CODE_FIELD,
    LINE_CODE_FIELD,
    LINE_NAME_FIELD,
    MANDATED_FIELD,
    NATURE_FIELD,
    NATURE_LABEL_FIELD,
    OPERATION_FIELD,
    OPERATION_NAME_FIELD,
    OPERATION_SEGMENT_CODE,
    OPERATION_SEGMENT_NAME,
    ORG_CHART_FINANCE_CODE_FIELD,
    ORG_CHART_FINANCE_FIELD,
    PERCENT_REALIZED_FIELD,
    PRIMITIVE_BUDGET_AMOUNT,
    REAL_ORDER_FIELD,
    REALIZED_FIELD,
    REPORTED_FIELD,
    REQUESTED_FIELD,
    SERVICE_PROPOSAL_AMOUNT,
    SIMULATED_ARBITRATED_AMOUNT,
    STRAT_FOCUS_CODE,
    STRAT_FOCUS_NAME,
    SUB_NATURE_FIELD,
    SUB_NATURE_LABEL_FIELD,
    SUPER_GESTIONNAIRE_CODE_FIELD,
    SUPER_GESTIONNAIRE_FIELD,
    SUPER_OPERATION_FIELD,
    SUPER_OPERATION_NAME_FIELD,
    SUPPLEMENTARY_BUDGET_AMOUNT,
    VOTED_FIELD,
    VOTED_TOTAL_FIELD,
    ANALYTIC_NATURE_CODE,
    ANALYTIC_NATURE_NAME,
    SECTION_FIELD,
} from 'routes/BudgetModule/config/constants';
import { realizedRateAggFunc } from 'ui/AgGridReact/aggFuncs';
import { isANumber } from 'ui/AgGridReact/helpers';
import { SUPPLEMENTARY_BUDGET_COLUMN_FIELD } from './colDefinitions';

export type CustomAggValue = {
    totalAmountRequested: number;
    totalAmountVoted: number;
};

const evolutionAggFunc = (
    params: IAggFuncParams<CustomAggValue[]>,
): {
    totalAmountRequested: number | null;
    totalAmountVoted: number | null;
} => {
    let totalAmountRequestedSum: number | null = null;
    let totalAmountVotedSum: number | null = null;

    // the next line is very useful to debug and understand what is going on in evolution
    // console.log(params.rowNode.rowGroupIndex, params.rowNode.key, params.values);

    params.values.forEach((value) => {
        if (!value) return;

        if (isANumber(value.totalAmountRequested)) {
            if (totalAmountRequestedSum === null) {
                totalAmountRequestedSum = 0;
            }
            totalAmountRequestedSum += value.totalAmountRequested;
        }
        if (isANumber(value.totalAmountVoted)) {
            if (totalAmountVotedSum === null) {
                totalAmountVotedSum = 0;
            }
            totalAmountVotedSum += value.totalAmountVoted;
        }
    });

    return {
        totalAmountRequested: totalAmountRequestedSum,
        totalAmountVoted: totalAmountVotedSum,
    };
};

// registering aggFuncs to be able to reference them as strings, and JSON serialize them (for the API)
// see doc here (option 2): https://www.ag-grid.com/react-grid/aggregation/#defining-aggregations
export const aggFuncs: Record<string, IAggFunc> = {
    evolutionAggFunc,
    realizedRateAggFunc,
};

/**
 * Retrieves the default grid column ordering for the analysis view.
 *
 * @returns an array of field names representing the
 * default order in which grid columns should be displayed.
 */
export const getAnalysisDefaultGridColumnsOrdering = (): ColDef['field'][] => [
    BUDGET_LINE_PROGRAM_CODE,
    BUDGET_LINE_PROGRAM_NAME,
    ANALYTIC_NATURE_CODE,
    ANALYTIC_NATURE_NAME,
    CHAPTER_FIELD,
    CHAPTER_LABEL_FIELD,
    ANALYTIC_FIELD,
    ANALYTIC_NAME_FIELD,
    ANTENNA_FIELD,
    ANTENNA_NAME_FIELD,
    BUDGET_CODE_FIELD,
    BUDGET_NAME_FIELD,
    EXPENSE_INCOME_FIELD,
    FUNCTION_FIELD,
    FUNCTION_LABEL_FIELD,
    DIRECTION_CODE,
    DIRECTION_LABEL,
    GESTIONNAIRE_CODE_FIELD,
    GESTIONNAIRE_FIELD,
    SUPER_GESTIONNAIRE_CODE_FIELD,
    SUPER_GESTIONNAIRE_FIELD,
    ORG_CHART_FINANCE_CODE_FIELD,
    ORG_CHART_FINANCE_FIELD,
    SECTION_FIELD,
    NATURE_FIELD,
    NATURE_LABEL_FIELD,
    SUB_NATURE_FIELD,
    SUB_NATURE_LABEL_FIELD,
    OPERATION_FIELD,
    OPERATION_NAME_FIELD,
    SUPER_OPERATION_FIELD,
    SUPER_OPERATION_NAME_FIELD,
    OPERATION_SEGMENT_CODE,
    OPERATION_SEGMENT_NAME,
    AP_CODE,
    AP_LABEL,
    AP_START_YEAR,
    STRAT_FOCUS_CODE,
    STRAT_FOCUS_NAME,
    ANALYTIC_POST_0_CODE,
    ANALYTIC_POST_0_NAME,
    ANALYTIC_POST_1_CODE,
    ANALYTIC_POST_1_NAME,
    ANALYTIC_POST_2_CODE,
    ANALYTIC_POST_2_NAME,
    ANALYTIC_POST_3_CODE,
    ANALYTIC_POST_3_NAME,
    ANALYTIC_POST_4_CODE,
    ANALYTIC_POST_4_NAME,
    ANALYTIC_POST_5_CODE,
    ANALYTIC_POST_5_NAME,
    ANALYTIC_POST_6_CODE,
    ANALYTIC_POST_6_NAME,
    ANALYTIC_POST_7_CODE,
    ANALYTIC_POST_7_NAME,
    ANALYTIC_POST_8_CODE,
    ANALYTIC_POST_8_NAME,
    ANALYTIC_POST_9_CODE,
    ANALYTIC_POST_9_NAME,
    REAL_ORDER_FIELD,
    LINE_CODE_FIELD,
    LINE_NAME_FIELD,
    BUDGET_LINE_CODE,
    ENVELOPPE_CODE,
    ENVELOPPE_LABEL,
    BUDGET_YEAR_FIELD,
    AMOUNT_AVAILABLE_FIELD,
    VOTED_FIELD,
    REPORTED_FIELD,
    AMOUNT_AMENDING_DECISION_FIELD,
    AMOUNT_VOTED_SUP_FIELD,
    AMOUNT_SPECIAL_AUTHORIZATION_FIELD,
    VOTED_TOTAL_FIELD,
    MANDATED_FIELD,
    AMOUNT_ENGAGED_UNPAID,
    REALIZED_FIELD,
    PERCENT_REALIZED_FIELD,
    REQUESTED_FIELD,
    SERVICE_PROPOSAL_AMOUNT,
    EVOLUTION_N_N_1,
    SIMULATED_ARBITRATED_AMOUNT,
    PRIMITIVE_BUDGET_AMOUNT,
    SUPPLEMENTARY_BUDGET_COLUMN_FIELD,
    SUPPLEMENTARY_BUDGET_AMOUNT,
];
